'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { ChevronLeft } from 'lucide-react';
import { useBreakpoint } from '@/shared/hooks/use-media-query';
import { AccountSubpageNav } from '@/features/settings/ui/AccountSubpageNav';
import { MobileAccountHeader } from '@/features/settings/ui/MobileAccountHeader';
import {
  allAccountLinkItems,
  visibleNavigableAccountItems,
} from '@/features/settings/model/account-menu';
import { useSubscriptionLimits } from '@/features/subscription/model/subscription.selectors';
import { AppSidebar } from '@/features/project';
import { useSidebarStore } from '@/shared/stores/sidebar-store';
import { DesktopShell } from '@/shared/ui';

export default function AccountLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  const { isLg } = useBreakpoint();
  const sidebarCollapsed = useSidebarStore((s) => s.collapsed);
  const pathname = usePathname();
  // Branding (white-label) drops out of the pager for users without the
  // white_label entitlement — parity with the Account menu.
  const canUseWhiteLabel =
    useSubscriptionLimits()?.is_white_label_allowed ?? false;

  if (!isLg) {
    return (
      <>
        <MobileAccountHeader />
        {children}
      </>
    );
  }

  const showSubpageNav = visibleNavigableAccountItems({ canUseWhiteLabel }).some(
    (item) => pathname === item.href || pathname.startsWith(item.href + '/')
  );
  const currentMenuItem = allAccountLinkItems.find(
    (item) => pathname === item.href || pathname.startsWith(item.href + '/')
  );

  // Account is a single-pane, push-navigation surface on every width: /account
  // shows the menu list, /account/<sub> shows a detail with a back button.
  // There is no side-by-side settings-list + detail column — the user sees
  // EITHER the menu OR a detail, mirroring the Apple/Android single-column
  // layout. The content is centered and capped (`max-w-[760px]`) so that on a
  // wide window — with the app sidebar collapsed — it occupies roughly the
  // reading width it has with the sidebar shown instead of stretching
  // edge-to-edge (parity with the Help page, also capped at 760).
  return (
    <DesktopShell sidebar={<AppSidebar />}>
      {/* Collapsed RIGHT padding mirrors DesktopShell's LEFT gutter so the
          capped `max-w-[760px]` content re-centers, with the scrollbar staying
          at the window edge (padding is inside the border box). The collapsed
          state ALSO surfaces the floating bottom bar (MobileNavigation, fixed
          bottom-3), so add a bottom clearance (pb-24) on the scroll container —
          otherwise the last cards scroll under the bar and can never be fully
          revealed. */}
      <main
        className={`flex-1 min-w-0 overflow-y-auto${sidebarCollapsed ? ' pr-14 pb-24' : ''}`}
      >
        <div className="mx-auto w-full max-w-[760px]">
          {currentMenuItem && (
            <AccountSubpageHeader
              labelKey={currentMenuItem.labelKey}
              showNav={showSubpageNav}
              showBack
            />
          )}
          {children}
        </div>
      </main>
    </DesktopShell>
  );
}

/**
 * Sticky account-page header. Renders the page title (sourced from the same
 * sidebar label key, so header and sidebar stay in sync) on the left and the
 * prev/next chevron pager on the right — mirrors Apple's `navigationTitle`
 * + chevron toolbar layout.
 *
 * `showBack` enables a leading back-chevron that returns to the /account
 * menu list. It is only used in the compact-desktop layout where the
 * settings aside is hidden — wide layout has the aside as the navigation
 * affordance and doesn't need a back button.
 */
function AccountSubpageHeader({
  labelKey,
  showNav,
  showBack,
}: {
  labelKey: string;
  showNav: boolean;
  showBack: boolean;
}) {
  const t = useTranslations('account');
  return (
    <div className="sticky top-0 z-10 flex items-center justify-between gap-2 h-14 px-2 sm:px-4 lg:px-6 backdrop-blur-xl">
      <div className="flex items-center gap-1 min-w-0 flex-1">
        {showBack && (
          <Link
            href="/account"
            className="flex items-center justify-center h-9 w-9 rounded-full text-primary hover:bg-muted shrink-0"
            aria-label={t('title')}
          >
            <ChevronLeft className="h-5 w-5" />
          </Link>
        )}
        <h1 className="text-page-title text-foreground truncate">
          {t(labelKey)}
        </h1>
      </div>
      {showNav && <AccountSubpageNav />}
    </div>
  );
}
